import type { ServicesState } from 'src/context/AiContext';
import type { StockVehicle } from 'src/motornet/types';
import type { ConfiguratorProgress, OptionId, VehicleRef } from '../domain';
import type { OrChoice } from '../configurator';
import type { DatiVeicolo } from '../quotation';
import {
	type ConfigurationListing,
	type ConfigurationPricesAtSave,
	type LeasingFormSnapshot,
	type NewConfigurationDraft,
	type SavedConfigurationPayload,
	type SelectedOffer,
	type StockConfigurationDraft,
	type ServicesSelection,
	type StockPayload,
} from './types';

type Chain = { parentId: string | null };

const NO_CHAIN: Chain = { parentId: null };

export type ConfiguratorSnapshot = {
	makeCode: string | null;
	makeLabel: string | null;
	modelCode: string | null;
	modelLabel: string | null;
	vehicle: VehicleRef | null;
	tab: ConfiguratorProgress['tab'];
	committedPicks: OptionId[];
	choices: OrChoice[];
};

export type PricingSnapshot = {
	listPriceVat: number;
	optionalsTotalVat: number;
	totalVat: number;
};

export type ToRecordInput = {
	state: ConfiguratorSnapshot;
	pricing: PricingSnapshot;
	leasing: LeasingFormSnapshot;
	services: ServicesState;
	datiVeicolo: DatiVeicolo;
	imageUrl?: string | null;
	chain?: Chain;
};

export type ToStockRecordInput = {
	state: Pick<
		ConfiguratorSnapshot,
		'makeCode' | 'makeLabel' | 'modelCode' | 'modelLabel'
	>;
	stockVehicle: StockVehicle;
	leasing: LeasingFormSnapshot;
	services: ServicesState;
	chain?: Chain;
};

export type DownloadModalValues = {
	name?: string;
	clientType?: string;
	processedRow?: { original?: Record<string, unknown> } | null;
};

const checkedValue = (
	group: { value: string | number; isChecked: boolean }[]
): string | number | null => group.find((o) => o.isChecked)?.value ?? null;

export function readServices(services: ServicesState): ServicesSelection {
	const gomme = services.gomme.find((o) => o.isChecked);

	return {
		assicurazioneRC: checkedValue(services.assicurazioneRC),
		assicurazioneKasko: checkedValue(services.assicurazioneKasko),
		furtoIncendio: checkedValue(services.furtoIncendio),
		vetturaSostitutiva: checkedValue(services.vetturaSostitutiva),
		gomme: gomme
			? {
					value: gomme.value,
					unlimited: !!gomme.unlimited,
					quantity: gomme.quantity ?? 1,
					estive: gomme.data?.estive ?? 0,
					invernali: gomme.data?.invernali ?? 0,
			  }
			: null,
		tutelaLegale: services.tutelaLegale.isChecked,
		manutenzione: services.manutenzione.isChecked,
	};
}

export function applyServicesInPlace(
	base: ServicesState,
	selection: ServicesSelection
): ServicesState {
	const applyGroup = (
		group: { value: string | number; isChecked: boolean }[],
		value: string | number | null
	) => {
		if (value === null) {
			group.forEach((o) => (o.isChecked = false));
			return;
		}
		if (!group.some((o) => o.value === value)) return;
		group.forEach((o) => (o.isChecked = o.value === value));
	};

	applyGroup(base.assicurazioneRC, selection.assicurazioneRC);
	applyGroup(base.assicurazioneKasko, selection.assicurazioneKasko);
	applyGroup(base.furtoIncendio, selection.furtoIncendio);
	applyGroup(base.vetturaSostitutiva, selection.vetturaSostitutiva);

	const gomme = selection.gomme;
	if (gomme === null) {
		base.gomme.forEach((o) => (o.isChecked = false));
	} else {
		const target = base.gomme.find((o) => o.value === gomme.value);
		if (target) {
			base.gomme.forEach((o) => (o.isChecked = false));
			target.isChecked = true;
			target.unlimited = gomme.unlimited;
			target.quantity = gomme.quantity;
			target.data = { estive: gomme.estive, invernali: gomme.invernali };
		}
	}

	base.tutelaLegale.isChecked = selection.tutelaLegale;
	base.manutenzione.isChecked = selection.manutenzione;
	return base;
}

export function toRecord(input: ToRecordInput): NewConfigurationDraft {
	const { state, pricing, leasing, services, datiVeicolo } = input;
	const chain = input.chain ?? NO_CHAIN;
	if (!state.vehicle || !state.makeCode || !state.modelCode) {
		throw new Error('toRecord: nessun veicolo configurato da salvare');
	}

	const progress: ConfiguratorProgress = {
		step: 'summary',
		makeCode: state.makeCode,
		makeLabel: state.makeLabel,
		modelCode: state.modelCode,
		modelLabel: state.modelLabel,
		vehicle: state.vehicle,
		subsection: 'exterior-interior',
		tab: state.tab,
		selectedOptions: [...state.committedPicks],
		choices: [...state.choices],
	};

	const payload: SavedConfigurationPayload = {
		progress,
		leasing: { ...leasing },
		services: readServices(services),
		offer: null,
		fuel: datiVeicolo.alimentazione,
		currency: 'EUR',
		parentId: chain.parentId,
	};

	const listing: ConfigurationListing = {
		makeCode: state.makeCode,
		makeLabel: state.makeLabel ?? '',
		modelCode: state.modelCode,
		modelLabel: state.modelLabel ?? '',
		trimCode: state.vehicle.eurotaxCode,
		trimLabel: datiVeicolo.allestimento,
	};

	const pricesAtSave: ConfigurationPricesAtSave = {
		currency: 'EUR',
		listPriceNoVat: pricing.listPriceVat,
		optionalsNoVat: pricing.optionalsTotalVat,
		totalNoVat: pricing.totalVat,
	};

	return {
		vehicleType: 'new',
		vehicleStockId: null,
		payload,
		listing,
		pricesAtSave,
		vehicleRef: state.vehicle,
		imageUrl: input.imageUrl ?? null,
		parentId: chain.parentId,
	};
}

export function toStockRecord(
	input: ToStockRecordInput
): StockConfigurationDraft {
	const { state, stockVehicle, leasing, services } = input;
	if (!state.makeCode || !state.modelCode) {
		throw new Error('toStockRecord: nessun veicolo di stock da salvare');
	}
	const chain = input.chain ?? NO_CHAIN;

	const payload: StockPayload = {
		leasing: { ...leasing },
		services: readServices(services),
		offer: null,
		fuel: stockVehicle.specs.alimentazione ?? '',
		currency: stockVehicle.price.currency,
		parentId: chain.parentId,
	};

	const listing: ConfigurationListing = {
		makeCode: state.makeCode,
		makeLabel: state.makeLabel ?? stockVehicle.make,
		modelCode: state.modelCode,
		modelLabel: state.modelLabel ?? stockVehicle.model,
		trimCode: stockVehicle.vehicleCode,
		trimLabel: stockVehicle.trim,
	};

	const pricesAtSave: ConfigurationPricesAtSave = {
		currency: stockVehicle.price.currency,
		listPriceNoVat: stockVehicle.price.listPriceNoVat,
		optionalsNoVat: stockVehicle.price.optionalsNoVat,
		totalNoVat: stockVehicle.price.totalNoVat,
	};

	return {
		vehicleType: 'stock',
		vehicleStockId: stockVehicle.id,
		payload,
		listing,
		pricesAtSave,
		vehicleRef: null,
		imageUrl: stockVehicle.photos[0] ?? null,
		parentId: chain.parentId,
	};
}

const toNumber = (v: unknown): number => {
	const n = typeof v === 'string' ? Number(v) : v;
	return typeof n === 'number' && Number.isFinite(n) ? n : 0;
};

export function readSelectedOffer(values: DownloadModalValues): SelectedOffer {
	const raw = values.processedRow?.original ?? {};

	return {
		raw,
		noleggiatore: typeof raw.noleggiatore === 'string' ? raw.noleggiatore : '',
		canone: toNumber(raw.updated_canone),
		durata: toNumber(raw.durata),
		kmAnnui: toNumber(raw.km_annui),
		anticipo: toNumber(raw.anticipo),
		clientName: values.name ?? '',
		clientType: values.clientType ?? '',
	};
}

export const toProgress = (
	payload: SavedConfigurationPayload
): ConfiguratorProgress => ({
	...payload.progress,
	selectedOptions: [...payload.progress.selectedOptions],
	choices: [...payload.progress.choices],
});

const isNumber = (v: unknown): boolean =>
	typeof v === 'number' && Number.isFinite(v);

const isNumeric = (v: unknown): boolean =>
	isNumber(v) ||
	(typeof v === 'string' && v.trim() !== '' && isNumber(Number(v)));

const isLeasing = (v: unknown): boolean => {
	if (!v || typeof v !== 'object') return false;
	const l = v as Record<string, unknown>;
	return (
		isNumber(l.rentalDuration) &&
		isNumber(l.totalKm) &&
		isNumber(l.advancePayment) &&
		isNumeric(l.commissionValue) &&
		(l.commissionType === 'EURO' || l.commissionType === 'PERCENTAGE')
	);
};

const isVehicleRef = (v: unknown): boolean => {
	if (!v || typeof v !== 'object') return false;
	const r = v as Record<string, unknown>;
	return (
		typeof r.eurotaxCode === 'string' &&
		!!r.eurotaxCode &&
		typeof r.vehicleKind === 'string' &&
		isNumber(r.year) &&
		isNumber(r.month)
	);
};

const hasLeasingAndServices = (value: unknown): boolean => {
	if (!value || typeof value !== 'object') return false;
	const p = value as Record<string, unknown>;
	return isLeasing(p.leasing) && !!p.services && typeof p.services === 'object';
};

export function isValidPayload(
	value: unknown
): value is SavedConfigurationPayload {
	if (!hasLeasingAndServices(value)) return false;
	const p = value as Record<string, unknown>;

	const progress = p.progress as Record<string, unknown> | undefined;
	if (!progress || typeof progress !== 'object') return false;
	if (typeof progress.makeCode !== 'string' || !progress.makeCode) return false;
	if (typeof progress.modelCode !== 'string' || !progress.modelCode) {
		return false;
	}
	if (!isVehicleRef(progress.vehicle)) return false;
	if (!Array.isArray(progress.selectedOptions)) return false;
	if (!progress.selectedOptions.every((id) => isNumber(id))) return false;
	if (!Array.isArray(progress.choices)) return false;

	return true;
}

export const isValidStockPayload = (value: unknown): value is StockPayload => {
	return hasLeasingAndServices(value);
};
